import {
  LIFECYCLE_CANONICAL,
  LIFECYCLE_LABEL,
  LifecycleStatus,
} from "./enrichment-list.model";

/** How a package's catalog status reads to a marketing user:
 *  - preparing: inDesign / designed.
 *  - approved: active. In the catalog but not launched, so customers cannot
 *    buy it yet.
 *  - sellable: launched. The only state in which customers can buy it.
 *  - offSale: inActive / retired / obsolete / rejected. */
export type PackageStatusTone =
  "preparing" | "approved" | "sellable" | "offSale";

export interface PackageStatusView {
  status: LifecycleStatus;
  label: string;
  tone: PackageStatusTone;
}

export const PACKAGE_STATUS_TONE: Record<LifecycleStatus, PackageStatusTone> = {
  inDesign: "preparing",
  designed: "preparing",
  inTest: "preparing",
  active: "approved",
  launched: "sellable",
  inActive: "offSale",
  retired: "offSale",
  obsolete: "offSale",
  rejected: "offSale",
  deleted: "offSale",
};

/** Same hues as the list's `.status-pill` tones (designed / active / launched
 *  / retired), so one colour means one thing wherever a package status shows. */
export const PACKAGE_TONE_CLASS: Record<
  PackageStatusTone,
  { dot: string; text: string }
> = {
  preparing: { dot: "bg-[#3261ad]", text: "text-[#1f3f86]" },
  approved: { dot: "bg-[#8bc129]", text: "text-[#4a6b14]" },
  sellable: { dot: "bg-[#6fa31b]", text: "text-[#335f0d]" },
  offSale: { dot: "bg-[#9ca3af]", text: "text-[#4b5563]" },
};

export function canonicalStatus(
  value: string | undefined,
): LifecycleStatus | undefined {
  return LIFECYCLE_CANONICAL[value?.toLowerCase() ?? ""];
}

export function packageStatusView(
  value: string | undefined,
): PackageStatusView | undefined {
  const status = canonicalStatus(value);
  if (!status) return undefined;
  return {
    status,
    label: LIFECYCLE_LABEL[status],
    tone: PACKAGE_STATUS_TONE[status],
  };
}

/** What each tone means for selling, for a tooltip beside the status. */
export const PACKAGE_STATUS_HINT: Record<PackageStatusTone, string> = {
  preparing:
    "The product offering is still being prepared in the catalog. Customers cannot buy it yet.",
  approved:
    "The product offering is approved in the catalog but not launched. Customers cannot buy it yet.",
  sellable: "The product offering is launched. Customers can buy it.",
  offSale:
    "The product offering is off sale. Customers cannot buy it, whatever its prices say.",
};
